import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from 'src/api/db/apiAnalytics'
import { useSupabaseClient } from 'src/api/supabase'
import { Column } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  OnboardingAnchor,
  ONBOARDING_ELEMENT_IDS,
  ONBOARDING_STORAGE_KEYS,
  useOnboarding,
} from 'src/shared/hooks/useOnboarding'
import { myAccountRequestsPath, paths } from 'src/routes/paths'
import type { RequestTabId } from '../types'
import { RequestProvider, useRequestContext } from '../hooks/RequestContext'
import { useOrderViewState } from '../hooks/useOrderViewState'
import { OrderCatalogWidget } from './OrderCatalogWidget'
import { OrderCatalogControlProvider } from './OrderCatalogWidget/OrderCatalogControlContext'
import { ReadyToCheckoutCta } from './ReadyToCheckoutCta'
import { RequestBottomNav } from './RequestBottomNav'
import { RequestChatSidebar } from './RequestChatSidebar'
import { RequestFloatingNav } from './RequestFloatingNav'
import { RequestPageContent } from './RequestPageContent'
import { RequestUnavailable } from './RequestUnavailable'

const DEFAULT_TAB: RequestTabId = 'explore'

const RequestWorkspaceContent = ({
  exploreMode = false,
}: {
  exploreMode?: boolean
}) => {
  const { quote, quoteItems, isDemo, isError, isQuoteMissing, isQuotePending, refetchQuote, publicId } =
    useRequestContext()
  const { t } = useLocalization()
  const navigate = useNavigate()
  const supabase = useSupabaseClient()
  const { activeStep, acknowledge } = useOnboarding({
    storageKey: ONBOARDING_STORAGE_KEYS.request,
    steps: isDemo
      ? []
      : [
          {
            elementId: ONBOARDING_ELEMENT_IDS.requestChat,
            content: t(
              'Chat with Sara here. She helps you find products and build the order.',
              'Chatta med Sara här. Hon hjälper dig hitta produkter och bygga ordern.'
            ),
          },
          {
            elementId: ONBOARDING_ELEMENT_IDS.requestNav,
            requiresSeenIds: [ONBOARDING_ELEMENT_IDS.requestChat],
            ringClassName: 'rounded-full',
            content: t(
              'Your request moves through explore, checkout, and delivery.',
              'Din förfrågan går via utforska, kassa och leverans.'
            ),
          },
        ],
  })
  const viewBasePath = exploreMode
    ? paths.guidedAssistant
    : myAccountRequestsPath
  const {
    activeTab: urlTab,
    setTab,
    setView,
    hasViewInUrl,
  } = useOrderViewState(viewBasePath, {
    stackNestedViews: exploreMode,
  })
  const [catalogOpen, setCatalogOpen] = useState(false)

  const didInitView = useRef(false)
  useEffect(() => {
    if (hasViewInUrl || didInitView.current) return
    didInitView.current = true
    setView(DEFAULT_TAB)
    // Intentionally omit setView — write default view once when URL has none.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasViewInUrl])

  const activeTab = urlTab ?? DEFAULT_TAB
  const hasItems = (quoteItems?.length ?? 0) > 0

  useEffect(() => {
    if (exploreMode || isDemo || !quote?.id) return
    trackEvent(supabase, {
      name: 'request_open',
      quoteId: quote.id,
      onceKey: `request_open:${quote.id}`,
    })
  }, [exploreMode, isDemo, quote?.id, supabase])

  useEffect(() => {
    if (exploreMode || isDemo || !quote?.id || activeTab !== 'checkout') return
    trackEvent(supabase, {
      name: 'checkout_open',
      quoteId: quote.id,
      onceKey: `checkout_open:${quote.id}`,
    })
  }, [activeTab, exploreMode, isDemo, quote?.id, supabase])

  const isNestedView = activeTab === 'checkout' || activeTab === 'delivery'

  const handleGuidedBack = () => {
    if (isNestedView) {
      setTab(DEFAULT_TAB)
      return
    }
    const historyIdx = (
      window.history.state as { idx?: number } | null
    )?.idx
    if (typeof historyIdx === 'number' && historyIdx > 0) {
      navigate(-1)
      return
    }
    navigate(paths.home)
  }

  if (!isDemo && !isQuotePending && (isError || isQuoteMissing || !quote)) {
    return (
      <Column className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <RequestFloatingNav
          backHref={
            exploreMode ? undefined : paths.myAccount + paths.requests
          }
          onBack={exploreMode ? handleGuidedBack : undefined}
          exploreMode={exploreMode}
        />
        <RequestUnavailable
          publicId={publicId}
          isMissing={isQuoteMissing || !isError}
          onRetry={refetchQuote}
          onBack={exploreMode ? handleGuidedBack : undefined}
        />
      </Column>
    )
  }

  const handleCatalogOpenChange = (open: boolean) => {
    setCatalogOpen(open)
    if (!open || exploreMode || isDemo || !quote?.id) return
    trackEvent(supabase, {
      name: 'catalog_open',
      quoteId: quote.id,
    })
  }

  return (
    <OrderCatalogControlProvider
      value={{
        openCatalog: () => handleCatalogOpenChange(true),
        hasItems,
      }}
    >
      <Column className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <RequestFloatingNav
          backHref={
            exploreMode ? undefined : paths.myAccount + paths.requests
          }
          onBack={exploreMode ? handleGuidedBack : undefined}
          exploreMode={exploreMode}
        />

        <Column className="relative min-h-0 flex-1 overflow-hidden">
          <RequestPageContent activeTab={activeTab} />
          <RequestChatSidebar activeTab={activeTab} />
          {activeTab === 'explore' && (
            <ReadyToCheckoutCta
              hasItems={hasItems}
              onGoToCheckout={() => setTab('checkout')}
            />
          )}
          <RequestBottomNav activeTab={activeTab} onTabChange={setTab} />
        </Column>

        {quote?.id != null && (
          <OrderCatalogWidget
            quoteId={quote.id}
            open={catalogOpen}
            onOpenChange={handleCatalogOpenChange}
          />
        )}
      </Column>
      <OnboardingAnchor
        activeStep={activeStep}
        onAcknowledge={acknowledge}
        measureKey={`${quote?.id ?? 'pending'}:${activeTab}`}
      />
    </OrderCatalogControlProvider>
  )
}

export const RequestWorkspace = ({
  publicId,
  exploreMode = false,
}: {
  publicId?: string
  exploreMode?: boolean
}) => {
  return (
    <RequestProvider publicId={publicId}>
      <RequestWorkspaceContent exploreMode={exploreMode} />
    </RequestProvider>
  )
}
